import type { IncludeExcludeState } from '@pulsepointinc/bi-components';
import { format, subDays } from 'date-fns';
import { DEFAULT_DASHBOARD_PARAMS } from '../components/LookerExtension/consts';
import type { WorkspaceEnvironmentKeys } from '../lib/api/workspaces';
import type {
  FilterConfigGroupItem,
  FilterConfigItem,
  FilterConfigSubGroupItem,
} from '../modules/GenomeWorkspaceView/LeftView/components/FilterConfig/FilterConfigTypes';
import type { ExpressionType, Filter } from '../stores/filters';
import { Environment } from './enums';

export interface ICustomFilter {
  onExpressionTypeChange?: (value: string) => void;
  onChange?: (value: string | Record<string, Filter>) => void;
  expression?: string;
  expressionType?: ExpressionType;
  isAdvancedFilter?: boolean;
  filterName?: string;
}
export type SuggestionItem = { listName: string; listAlias?: string };

export const transformFilterExpression = (isInclude: boolean, filterExpression: string) => {
  if (
    !isInclude &&
    !!filterExpression &&
    !filterExpression.startsWith('not') &&
    !filterExpression
      ?.split(',')
      .map((el) => el.startsWith('-'))
      .every((val) => val === true)
  ) {
    let transformedExpression: string;

    if (filterExpression.startsWith('[') && filterExpression.endsWith(']')) {
      transformedExpression = `not${filterExpression}`;
    } else {
      transformedExpression = filterExpression
        ?.split(',')
        .map((i) => (i.endsWith(']') ? i : `-${i}`))
        .join(',');
    }

    return transformedExpression;
  }

  if (
    isInclude &&
    filterExpression &&
    filterExpression
      ?.split(',')
      .map((el) => el.startsWith('-'))
      .every((val) => val === true)
  ) {
    return filterExpression
      ?.split(',')
      .map((el) => el.slice(1))
      .join(',');
  }
  return filterExpression;
};

export const transformFiltersMapToObject = (
  filtersList?: Filter[],
  outputName?: 'filterName' | 'lookml',
  filterConfigItems?: FilterConfigItem[],
) => {
  if (!filtersList || !filterConfigItems) {
    return {};
  }

  const validFilters = filtersList.filter((filter) =>
    filterConfigItems.find((item) => item.filterName === filter.filterName),
  );

  const selectedFilters = validFilters?.map((filter) => {
    const configStrings = ['config_string', 'config_care_team_string', 'config_affiliated_graph_string'];

    const indexName = (() => {
      if (configStrings.includes(filter.filterName)) {
        return `genome_hcp_template.${filter.filterName}`;
      }
      if (filter.filterName.includes('_advanced')) {
        return '';
      }

      if (outputName === 'lookml') {
        const foundItem = filterConfigItems.find((f) => f.filterName === filter.filterName);

        const indexNameToUse = foundItem?.lookmlAddress || filter.filterName || '';

        return indexNameToUse;
      }

      return filter.filterName;
    })();

    if (filter.expressionType === 'yesno' && ['Yes', 'No'].includes(filter.expression)) {
      return {
        [indexName]: filter.expression === 'Yes' ? 'Yes' : 'No',
      };
    }

    if (!filter?.enabled || (filter?.enabled && (filter.expression === '' || !filter.expression))) {
      return { [indexName]: '' };
    }

    return {
      [indexName]: transformFilterExpression(filter?.expressionType === 'target', filter.expression),
    };
  });

  const filteredArray = selectedFilters?.filter((obj) => {
    const filterName = Object.keys(obj)[0];
    return !!filterName && filterName !== 'undefined';
  });

  const resultObject = Object.assign({}, ...filteredArray);
  return resultObject;
};

export const buildFiltersQueryString = (filterArray: Filter[]) => {
  const enabledFilters = filterArray.filter((filter) => filter.enabled && filter.filterName && filter.expression);

  const queryStringComponents = enabledFilters.map((filter) => {
    const filterExpression: string =
      filter.expressionType === 'yesno'
        ? filter.expression
        : transformFilterExpression(filter.expressionType === 'target', filter.expression);

    return `${encodeURIComponent(filter.filterName)}=${encodeURIComponent(filterExpression)}`;
  });

  const defaultStringComponents = Object.entries(DEFAULT_DASHBOARD_PARAMS).map(
    ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
  );

  queryStringComponents.push(...defaultStringComponents);

  return queryStringComponents.length > 0 ? `?${queryStringComponents.join('&')}` : '';
};

export const transformIncludeExcludeToStringExpression = (state: IncludeExcludeState): string => {
  return Object.keys(state)
    .map((category) => {
      const { check, block } = state[category];

      if (check && !block) {
        return category;
      }
      if (block) {
        return `-${category}`;
      }

      return '';
    })
    .filter(Boolean)
    .join(',');
};

export const transformStringExpressionToIncludeExclude = (
  expression: string,
): Record<string, { check: boolean; block: boolean }> => {
  const categories = expression.split(',');
  const state: Record<string, { check: boolean; block: boolean }> = {};

  categories.forEach((category) => {
    const trimmedCategory = category.trim();
    const isExcluded = trimmedCategory.startsWith('-');
    const categoryName = isExcluded ? trimmedCategory.slice(1) : trimmedCategory;

    state[categoryName] = {
      check: !isExcluded,
      block: isExcluded,
    };
  });

  return state;
};

export const cleanExpression = (expression: string) => {
  return expression
    .replace(/[[\]]/g, '')
    .replace(/^not\s*/, '')
    .replace(',', ' to ');
};

export const rangeExpressionToArray = (expression?: string) => {
  if (!expression) return [];

  return expression
    .replace(/\[|\]/g, '')
    .split(',')
    .map((value) => parseInt(value.trim(), 10));
};

export const dateRangeFromDays = (days: number) => {
  const formattedStart = format(subDays(new Date(), days), 'yyyy/MM/dd');
  const formattedEnd = format(new Date(), 'yyyy/MM/dd');
  return `${formattedStart} to ${formattedEnd}`;
};

export const getSafeJSONParsedValue = (value?: string) => {
  if (!value) {
    return '';
  } else {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error(error);
      return '';
    }
  }
};

export const cleanFilters = (
  filters: { [key: string]: Filter },
  filterConfigItems: FilterConfigItem[],
  isExpansionWorkspace?: boolean,
): Filter[] => {
  const preservedFilterNames = filterConfigItems
    .filter((f) => {
      if (isExpansionWorkspace) {
        return f.filterToPreserveOnReset || f.type.includes('accordion_list');
      }
      return f.filterToPreserveOnReset;
    })
    .map((f) => f.filterName);

  return Object.values(filters)
    .filter((filter) => filter.enabled && !preservedFilterNames.includes(filter.filterName))
    .map((filter) => ({
      ...filter,
    }));
};

export const getEnvironment = (url?: string): WorkspaceEnvironmentKeys | '' => {
  if (!url) {
    return '';
  }

  if (url.includes('dev')) {
    return 'dev';
  } else if (url.includes('stg')) {
    return 'stg';
  } else {
    return 'prod';
  }
};

export const EnvironmentMap: Record<WorkspaceEnvironmentKeys, Environment> = {
  dev: Environment.DEVELOPMENT,
  stg: Environment.STAGING,
  prod: Environment.PRODUCTION,
};

export const getFilterType = (filterName: string, filterConfigItems: FilterConfigItem[]): string | undefined => {
  const matchingFilterItem = filterConfigItems.find((item) => item.filterName === filterName);
  return matchingFilterItem?.type;
};

export const truncateExpr = /,(?=(?:[^"]*"[^"]*")*[^"]*$)/g;

export const getGroupName = (
  filterName: string,
  filterConfigGroups: FilterConfigGroupItem[],
  filterConfigSubGroups: FilterConfigSubGroupItem[],
): string => {
  if (!filterName) return '';

  const matchingGroup = filterConfigGroups.find((group) =>
    group.filtersInGroup?.some((filter) => filter.name === filterName),
  );
  if (matchingGroup) return matchingGroup.groupLabel;

  const matchingSubGroup = filterConfigSubGroups.find(
    (subGroup) =>
      subGroup.groupLabel === filterName || subGroup.filtersInGroup.some((filter) => filter.name === filterName),
  );
  if (matchingSubGroup) return matchingSubGroup.parentGroupLabel;

  return '';
};

export const mapAdvertiserName = <T extends number | number[]>(
  ids: T,
  advertisers: { id: number; value: string }[],
): T extends number ? string : string[] => {
  const findAdvertiserName = (id: number): string => {
    const advertiser = advertisers.find((adv) => adv.id === id);
    return advertiser ? advertiser.value : String(id);
  };

  if (Array.isArray(ids)) {
    return ids.map(findAdvertiserName) as T extends number ? string : string[];
  } else {
    return findAdvertiserName(ids) as T extends number ? string : string[];
  }
};

export const cleanNpiListName = (value: string): string => {
  if (!value) return '';

  return value
    .replace(/\[Type:.*?\]/g, '')
    .replace(/\(.*?\)/g, '')
    .replace(/＿/g, '_')
    .trim();
};

export const formatNumberWithCommas = (value: number | null | undefined): string => {
  if (!value || Number.isNaN(value)) {
    return '';
  }

  return value.toLocaleString('en-US');
};

export const mapAliasNameKey = (
  inputState: IncludeExcludeState,
  groupedSuggestions: Record<string, SuggestionItem[]>,
  mapAliasToName: boolean,
): IncludeExcludeState => {
  if (!groupedSuggestions) {
    return inputState;
  }

  return Object.entries(inputState).reduce((acc, [key, value]) => {
    for (const group in groupedSuggestions) {
      const foundItem = groupedSuggestions[group].find((item) =>
        mapAliasToName ? item.listAlias === key : item.listName === key,
      );

      if (mapAliasToName && foundItem?.listName) {
        acc[foundItem.listName] = value;
        return acc;
      }

      if (!mapAliasToName && foundItem?.listAlias) {
        acc[foundItem.listAlias] = value;
        return acc;
      }
    }

    acc[key] = value;
    return acc;
  }, {} as IncludeExcludeState);
};

export const downloadFromUrl = (url: string, filename: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
};

export class DownloadError extends Error {
  downloadUrl: string;
  filename: string;

  constructor(message: string, downloadUrl: string, filename: string) {
    super(message);
    this.name = 'DownloadError';
    this.downloadUrl = downloadUrl;
    this.filename = filename;
  }
}

export const safeDownloadFromUrl = async (url: string, filename: string): Promise<void> => {
  try {
    const response = await fetch(url, { method: 'GET' });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    }, 100);
  } catch (error) {
    console.error('Safe download failed, providing download URL:', error);
    throw new DownloadError('Manual download required', url, filename);
  }
};

export const isFilterVisible = (
  filter: Filter,
  isExpansionWorkspace: boolean,
  filtersCollection: FilterConfigItem[],
) => {
  if (!filter || filter.visible === false) {
    return false;
  }

  const matchedFilterItem = filtersCollection.find((item) => item.filterName === filter.filterName);

  if (isExpansionWorkspace && matchedFilterItem?.type?.includes('accordion_list')) {
    return false;
  }

  const isSecondaryOrAuxiliary = filtersCollection.find(
    (c) => c.secondaryFilters?.includes(filter.filterName) || c.auxiliaryFilters?.includes(filter.filterName),
  );

  if (isSecondaryOrAuxiliary) {
    return false;
  }

  return !matchedFilterItem || matchedFilterItem.visible !== false;
};

export const mergeUpdatedFilters = (
  currentFilters: Record<string, Filter>,
  updatedFilters: Filter[],
): Record<string, Filter> => {
  const merged = { ...currentFilters };

  for (const filter of updatedFilters) {
    merged[filter.filterName] = filter;
  }

  return merged;
};

export const isNotNullish = <T>(x: T | undefined | null): x is T => x !== null && x !== undefined;
